<template>
  <div class="flex-1 flex flex-col justify-between">
    <div class="divide-gray-200">
      <div>
        <h2 class="text-lg leading-6 font-medium text-gray-700 pb-6 dark:text-white">
          {{ group_type == "suggested" ? $t("Create Suggested List") : $t("Create Growth Matrix") }}
        </h2>
      </div>
      <Form v-slot="{ meta }"
            :initial-values="initial_values"
            :validation-schema="schema"
            class="space-y-3"
            @submit="createList">
        <div class="rounded-md shadow-sm">
          <TextInput
            label=""
            name="name"
            type="text"
            :placeholder="group_type == 'suggested' ? $t('Suggested list name') : $t('Growth matrix name')" />
        </div>
        <div class="rounded-md shadow-sm">
          <TextArea label="" name="description" :placeholder="$t('Description')" />
        </div>
        <div
          name="filter"
          :class="[!is_filter_valid && meta.touched ? 'border-red-400' : '']"
          class="p-2 rounded-md flex flex-col border-[1px] border-gray-300 dark:border-dark-divider-border">
          <div class="h-full pb-8" style="min-height: 8rem">
            <AdvancedMetricsFilterForm
              :object-type="objectType"
              :initial-filter="current_filter"
              @update="
                ($event) => {
                  handleUpdateFilter($event)
                }
              " />
          </div>
          <span v-if="!is_filter_valid && meta.touched" class="inline text-[12px] pt-1 pl-1 float-left text-red-400">
            {{ current_filter?.length == 0 ? "Filter is required" : "Filter is invalid" }}
          </span>
          <p v-else-if="is_filter_valid" class="inline text-[12px] pt-1 pl-1 float-left font-normal text-gray-500">
            {{ $t("Write a formula to indicate that the") }}
            {{ group_type == "suggested" ? $t("suggested list") : $t("growth matrix") }} {{ $t("is achieved") }}
          </p>
        </div>
        <div class="flex justify-end">
          <button type="submit" class="primary-btn w-24">{{ $t("Create") }}</button>
          <button type="button" class="cancel-btn w-24 ml-1" @click="$emit('cancel')">{{ $t("Cancel") }}</button>
        </div>
        <div class="h-32" />
      </Form>
    </div>
  </div>
</template>

<script>
import * as Yup from "yup"
import TextInput from "@/components/shared/TextInput.vue"
import TextArea from "@/components/shared/TextArea.vue"
import { Form } from "vee-validate"
import dataService from "@/services/data.service"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import AdvancedMetricsFilterForm from "@/components/forms/AdvancedMetricsFilterForm.vue"
import { transformMetricFilterToServerFilter } from "@/services/data.transformers"
export default {
  components: {
    TextInput,
    TextArea,
    AdvancedMetricsFilterForm,
    Form
  },
  props: {
    objectType: {
      type: String,
      required: true
    },
    groupType: {
      type: String,
      required: true
    },
    metricsList: {
      type: Array,
      required: true
    }
  },
  emits: ["cancel", "done"],
  setup() {
    const schema = Yup.object().shape({
      name: Yup.string().required(),
      description: Yup.string(),
      is_public: Yup.mixed().oneOf(["true", "false"])
    })
    const initial_values = { name: "", description: "", is_public: "true", filter: "" }

    return {
      schema,
      initial_values
    }
  },

  data() {
    return {
      group_type: null,
      is_filter_valid: false,
      current_filter: ""
    }
  },
  created() {
    this.group_type = this.groupType
  },
  methods: {
    handleUpdateFilter(filter) {
      const filter_cleaned = []
      filter.forEach((item) => {
        if (item.data_type == "String") {
          if (Array.isArray(item.value)) {
            item.value = String(item.value)
          }
          // let value = item.value.replace(/[']+/g, "")
          filter_cleaned.push({
            ...item,
            value: item.value == null ? "" : item.value.trim()
          })
        } else {
          if (
            item.value == null ||
            item.value == undefined ||
            item.value == "" ||
            item.value == "null" ||
            item.value == "undefined" ||
            item.value == "None"
          ) {
            item.value = null
          } else if (
            (item.data_type == "Date" || item.data_type == "DateTime") &&
            !(
              item.operator.value == "__DAYS_WITHIN_LAST_X_DAYS__" ||
              item.operator.value == "__DAYS_MORE_THAN_X_DAYS_AGO__" ||
              item.operator.value == "__DAYS_EXACT_DAYS_AGO__" ||
              item.operator.value == "__DAYS_MORE_THAN_X_DAYS_FROM_NOW__" ||
              item.operator.value == "__DAYS_WITHIN_NEXT_X_DAYS__" ||
              item.operator.value == "__DAYS_EXACT_DAYS_FROM_NOW__"
            )
          ) {
            item.value = this.$moment(item.value).format("YYYY-MM-DD")
          } else if (
            item.data_type == "DateTime" &&
            !(
              item.operator.value == "__DAYS_WITHIN_LAST_X_DAYS__" ||
              item.operator.value == "__DAYS_MORE_THAN_X_DAYS_AGO__" ||
              item.operator.value == "__DAYS_EXACT_DAYS_AGO__" ||
              item.operator.value == "__DAYS_MORE_THAN_X_DAYS_FROM_NOW__" ||
              item.operator.value == "__DAYS_WITHIN_NEXT_X_DAYS__" ||
              item.operator.value == "__DAYS_EXACT_DAYS_FROM_NOW__"
            )
          ) {
            item.value = this.$moment(item.value).format("YYYY-MM-DDTHH:mm:ss")
          }
          filter_cleaned.push({ ...item, value: item.value })
        }
      })
      let parsed_filter = transformMetricFilterToServerFilter([...filter_cleaned])

      this.current_filter = parsed_filter.filter
      this.is_filter_valid = parsed_filter.valid
    },
    createList(values) {
      const solid_metrics_calc = []
      const stat_metrics_calc = []
      this.metricsList.forEach((m) => {
        if (m.id === "s1") {
          solid_metrics_calc.push({ name: this.$t("Actual value"), id: "s1" })
        } else if (m.id === "s2") {
          solid_metrics_calc.push({ name: this.$t("Forecasted value"), id: "s2" })
        } else {
          stat_metrics_calc.push(m.id)
        }
      })

      if (this.group_type == "suggested") {
        values["group_type"] = "suggested"
      } else {
        values["group_type"] = "growth_matrix"
      }
      values["filter"] = this.current_filter
      values["object_type"] = this.$utils.getObjectTypeByType(this.objectType).id

      values["displayed_metrics_stats"] = stat_metrics_calc

      if (this.is_filter_valid && this.current_filter.length != 1) {
        dataService.createGroup(values).then((res) => {
          if (this.group_type == "suggested") {
            this.$store.dispatch("data/updateSuggestedSolidMetrics", {
              id: res.data.id,
              list: solid_metrics_calc
            })
          } else {
            this.$store.dispatch("data/updateGrowthSolidMetrics", {
              id: res.data.id,
              list: solid_metrics_calc
            })
          }
          if (this.group_type == "suggested") {
            this.$emitter.emit(ALL_EMITTERS.SUGGESTED_LIST_CREATED_IN_SUGGESTED_LISTS_COMPONENT, res.data)
            this.$emitter.emit(ALL_EMITTERS.SUGGESTED_LIST_CREATED_IN_PROSPECTING_COMPONENT, res.data)
          } else {
            this.$emitter.emit(ALL_EMITTERS.GROWTH_MATRIX_CREATED_IN_GROWTH_MATRIX_COMPONENT)
            this.$emitter.emit(ALL_EMITTERS.GROWHT_MATRIX_CREATED_IN_SWOT_VIEW)
          }
          this.$toast.open({
            message:
              `${this.group_type == "suggested" ? this.$t("Suggested List") : this.$t("Growth Matrix")}` +
              " " +
              this.$t("created successfully"),
            position: "top",
            type: "success",
            duration: 3000
          })
          this.$emit("done", res.data)
        })
      } else {
        this.is_filter_valid = false
      }
    }
  }
}
</script>
